import type { Schema } from "./api";
import { jobProvider, NO_FALLBACK } from "./analysisLaunch.ts";

/** How a processing job reads to the estimator: Russian labels for the journal and the notice
 * for a stopped analysis. Only pure imports, so it runs under the plain Node test runner. */
type Job = Schema["JobOut"];
export type PauseKind = NonNullable<Job["pause_kind"]>;

export const jobStatusLabel: Record<string, string> = {
  queued: "В очереди",
  waiting: "Ждёт обработки всех файлов",
  running: "Выполняется",
  done: "Готово",
  paused: "Приостановлено",
  waiting_budget: "Ждёт увеличения предела расходов",
  cancelled: "Остановлено",
  failed: "Не завершено",
};

export const jobKindLabel: Record<string, string> = {
  analyze: "Анализ",
  ingest: "Пакет",
  extract: "Чтение файла",
  publish: "Страницы чертежа",
  preview: "Изображение источника",
  geometry: "Сверка АР и ЭОМ",
  thumbnails: "Миниатюры страниц",
  export: "Подготовка выгрузки",
  final_review: "Полная проверка комплекта",
};

export const pauseKindLabel: Record<PauseKind, string> = {
  budget: "предел расходов объекта",
  account: "баланс аккаунта OpenAI",
  reconciliation: "сверка расхода",
  provider_retryable: "провайдер модели",
  output_limit: "предел ответа модели",
  no_key: "ключ API модели",
  cancelled: "остановлено пользователем",
  document: "файл",
  server: "ошибка сервера",
  codex_auth: "подключение Codex",
  codex_limit: "лимит подписки ChatGPT",
  review: "есть нерешённые замечания",
};

export function jobKindText(job: Pick<Job, "kind" | "details" | "provider">) {
  const kind = job.kind === "analyze" && job.details.mode === "norms" ? "Подбор норм" : jobKindLabel[job.kind] ?? "Обработка";
  return ["analyze", "final_review"].includes(job.kind) && jobProvider(job) === "codex" ? `${kind} · Codex` : kind;
}

export function jobStatusText(job: Pick<Job, "status" | "pause_kind">) {
  const status = jobStatusLabel[job.status] ?? "Состояние не определено";
  return job.pause_kind && job.pause_kind !== "cancelled" && job.pause_kind !== "budget"
    ? `${status}: ${pauseKindLabel[job.pause_kind]}` : status;
}

export type StopNotice = {
  role: "status" | "alert";
  title: string;
  /** The server's own text, unless the notice already says it in the estimator's words. */
  message: string;
  /** What happened in plain words and who can fix it. */
  advice: string;
  /** stop_auto withdraws the retry the queue scheduled for a subscription reset. */
  action: "resume" | "files" | "stop_auto" | null;
  actionLabel: string;
  /** Technical record for the administrator, folded away. */
  detail: string;
};

const SAVED = "Готовые результаты сохранены.";
const clock = (value: string) => new Date(value).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" });

/** The notice above the statement for the latest analysis, or null while it runs, is done or was stopped by hand. */
export function stopNotice(job: Job | undefined, canEdit: boolean, time: (value: string) => string = clock): StopNotice | null {
  if (!job || job.kind !== "analyze" || !["paused", "waiting_budget", "failed"].includes(job.status)) return null;
  const budget = job.budget, norms = job.details.mode === "norms", codex = jobProvider(job) === "codex";
  const proceed = norms ? "Продолжить подбор норм" : "Продолжить анализ";
  const base = { role: "status" as const, message: job.message, action: "resume" as const, actionLabel: proceed, detail: "" };
  const notice = ((): StopNotice => {
    switch (job.pause_kind) {
      case "budget": {
        const mine = !!budget?.can_change_limit;
        return { ...base, title: "Не хватает предела расходов объекта.",
          advice: mine ? "Увеличьте предел расходов — продолжится то же задание, а не новое."
            : `Предел расходов может увеличить ${budget?.created_by_name || "тот, кто запускал анализ"} или владелец.`,
          actionLabel: mine ? "Изменить предел и продолжить" : "Открыть расход обработки" };
      }
      case "account":
        return { ...base, role: "alert", message: "", title: "Баланс или лимит аккаунта OpenAI исчерпан.",
          advice: "Это счёт сервиса, а не предел расходов объекта: пополнить его может администратор сервиса. Предел расходов объекта не менялся. После пополнения продолжите то же задание." };
      case "reconciliation": {
        const fetchOnly = !!budget && budget.unknown_requests > 0 && budget.recoverable_requests === budget.unknown_requests;
        return fetchOnly
          ? { ...base, title: "Ответ модели оплачен, но ещё не получен.", actionLabel: "Получить ответ и продолжить",
            advice: "Продолжение заберёт сохранённый ответ у провайдера по его идентификатору; новый платный запрос при этом не отправляется. Резерв сохранён." }
          : { ...base, role: "alert", title: "Нужна сверка расхода последнего запроса.", actionLabel: "Открыть расход обработки",
            advice: "Сверку с журналом провайдера выполняет администратор сервиса. До сверки новые запросы не отправляются; резерв сохранён." };
      }
      // Codex notices say the state once in their own words; the worker's text stays in the journal.
      case "provider_retryable":
        return codex
          ? { ...base, message: "", title: "Сервис Codex временно недоступен.", advice: "От вас ничего не требуется: продолжите то же задание позже." }
          : { ...base, title: "Провайдер модели временно недоступен.", advice: "От вас ничего не требуется: продолжите то же задание позже. Списаний за неотправленные запросы нет." };
      case "codex_auth":
        // No login, or a model this ChatGPT account cannot use.
        return { ...base, role: "alert", message: "", title: "Codex недоступен.",
          advice: `Администратор сервиса должен проверить вход в ChatGPT и доступ к модели; затем продолжите то же задание. ${NO_FALLBACK}` };
      case "codex_limit":
        // Automatic continuation is promised only for a reset time the queue has actually scheduled.
        return job.next_resume_at
          ? { ...base, message: "", title: "Исчерпан лимит подписки ChatGPT.", action: "stop_auto", actionLabel: "Не продолжать автоматически",
            advice: `То же задание продолжится автоматически после ${time(job.next_resume_at)} — это время восстановления лимита по данным сервиса. ${NO_FALLBACK}` }
          : { ...base, message: "", title: "Исчерпан лимит подписки ChatGPT.",
            advice: `Автоматическое продолжение не запланировано: продолжите то же задание вручную после восстановления лимита. ${NO_FALLBACK}` };
      case "output_limit":
        return { ...base, title: "Ответ модели достиг предела.", advice: "Продолжение начнётся с сохранённого места." };
      case "no_key":
        return { ...base, role: "alert", title: "Ключ API модели не настроен или отклонён.", advice: "Настроить ключ может администратор сервиса. После этого продолжите то же задание." };
      case "document":
        return { ...base, role: "alert", title: "Файл не обработан.", advice: "Проверьте файл в материалах: его можно прочитать повторно или заменить.", action: "files", actionLabel: "Открыть материалы" };
      case "server":
        return { ...base, role: "alert", message: "", title: "Внутренняя ошибка обработки.",
          advice: "Исправить её может администратор сервиса: техническая запись сохранена. Можно попробовать продолжить то же задание.",
          detail: typeof job.details.error_detail === "string" ? job.details.error_detail : "" };
      default:
        return { ...base, role: job.status === "failed" ? "alert" : "status", title: job.status === "failed" ? "Анализ не завершён." : "Анализ приостановлен.",
          advice: "Продолжится то же задание с сохранённого места." };
    }
  })();
  if (norms) notice.title = notice.title.replace(/^Анализ/, "Подбор норм");
  notice.advice = `${notice.advice} ${SAVED}`;
  return canEdit ? notice : { ...notice, action: notice.action === "files" ? "files" : null };
}

/** Spent, reserved, available and the limit, as the server computed them. */
export function budgetLine(budget: Job["budget"], dollars: (value: string) => string) {
  return budget ? `Расчётный расход ${dollars(budget.spent_usd)} · резерв ${dollars(budget.reserved_usd)} · доступно ${dollars(budget.remaining_usd)} · предел ${dollars(budget.limit_usd)}` : "";
}
